import { c as _c } from "react/compiler-runtime";
import React, { useState, useEffect } from "react";
import type { TestCheck } from "./TestList";
import type { JobLogsResponse, JobDetailsResponse } from "@/hooks/useJobDetails";
import { useTestHistory, type TestHistoryRun } from "@/hooks/useTestHistory";
import { VirtualizedLogViewer } from "./VirtualizedLogViewer";
import { TestLogViewer } from "./TestLogViewer";
import { TestHistoryPanel } from "./TestHistoryPanel";
import { TestDetailHeader } from "./TestDetailHeader";
import { safeSessionStorage } from "@/lib/storage";
import { cn } from "@/lib/utils";
interface JunitTestDetailViewProps {
  test: TestCheck;
  // Logs - for full log viewer (optional, when job logs are available)
  logs?: JobLogsResponse;
  steps?: JobDetailsResponse["job"]["steps"];
  isLogsLoading?: boolean;
  // History tab props
  org: string;
  repository: string;
  runId: string;
  jobId: string;
  branch?: string | null;
  timeRangeHours: number;
  branchFilter: "all" | "main" | "current" | "main_and_current" | "latest_per_branch";
  currentBranch?: string | null;
  zoomStart: number | null;
  zoomEnd: number | null;
  onZoomChange: (start: number | null, end: number | null) => void;
  onTimeRangeChange: (hours: number) => void;
  onBranchFilterChange: (filter: "all" | "main" | "current" | "main_and_current" | "latest_per_branch") => void;
  isResizing: boolean;
  onNavigationStart?: (targetRunId: string, targetJobId: string) => void;
  onRunClick: (run: TestHistoryRun) => void;
  showCardErrors: boolean;
  actorMap?: Map<string, string>;
  isSuperuser?: boolean;
  // Max duration for progress bars
  maxDurationPerSuite?: Map<string, number>;
}
export const JunitTestDetailView = React.memo(function JunitTestDetailView(t0) {
  const $ = _c(94);
  const {
    test,
    logs,
    steps,
    isLogsLoading,
    org,
    repository,
    runId,
    jobId,
    timeRangeHours,
    branchFilter,
    currentBranch,
    zoomStart,
    zoomEnd,
    onZoomChange,
    onTimeRangeChange,
    onBranchFilterChange,
    isResizing,
    onNavigationStart,
    onRunClick,
    showCardErrors,
    actorMap,
    isSuperuser: t1,
    maxDurationPerSuite
  } = t0;
  const isSuperuser = t1 === undefined ? false : t1;
  const isJunitTest = test.test_type === "junit";
  let t2;
  if ($[0] !== isJunitTest || $[1] !== jobId) {
    t2 = () => {
      if (typeof window === "undefined") {
        return "history";
      }
      if (isJunitTest) {
        return "history";
      }
      const stored = safeSessionStorage.get(`testDetailTab_${jobId}`);
      if (stored === "logs" || stored === "history") {
        return stored;
      }
      return "history";
    };
    $[0] = isJunitTest;
    $[1] = jobId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [activeTab, setActiveTab] = useState(t2);
  let t3;
  let t4;
  if ($[3] !== activeTab || $[4] !== isJunitTest) {
    t3 = () => {
      if (isJunitTest && activeTab !== "history") {
        setActiveTab("history");
      }
    };
    t4 = [isJunitTest, activeTab];
    $[3] = activeTab;
    $[4] = isJunitTest;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[7] !== activeTab || $[8] !== jobId) {
    t5 = () => {
      safeSessionStorage.set(`testDetailTab_${jobId}`, activeTab);
    };
    t6 = [activeTab, jobId];
    $[7] = activeTab;
    $[8] = jobId;
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  useEffect(t5, t6);
  const t7 = test.test_suite ?? null;
  const t8 = test.test_name || "";
  const t9 = isJunitTest ? "junit" : "auto";
  const t10 = activeTab === "history" && !!org && !!repository && !!test.test_name;
  const t11 = currentBranch ?? undefined;
  let t12;
  if ($[11] !== branchFilter || $[12] !== org || $[13] !== repository || $[14] !== t10 || $[15] !== t11 || $[16] !== t7 || $[17] !== t8 || $[18] !== t9 || $[19] !== test.id || $[20] !== timeRangeHours) {
    t12 = {
      org,
      repository,
      testSuite: t7,
      testName: t8,
      testType: t9,
      enabled: t10,
      timeRangeHours,
      branchFilter,
      currentBranch: t11,
      testId: test.id,
      includeSimilarity: true
    };
    $[11] = branchFilter;
    $[12] = org;
    $[13] = repository;
    $[14] = t10;
    $[15] = t11;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
    $[19] = test.id;
    $[20] = timeRangeHours;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const {
    data: testHistory,
    isLoading: isLoadingHistory,
    isFetching: isFetchingHistory
  } = useTestHistory(t12);
  let t13;
  if ($[22] !== maxDurationPerSuite || $[23] !== test.duration_seconds || $[24] !== test.test_suite) {
    t13 = maxDurationPerSuite?.get(test.test_suite || "") || test.duration_seconds || 0;
    $[22] = maxDurationPerSuite;
    $[23] = test.duration_seconds;
    $[24] = test.test_suite;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const maxDuration = t13;
  const hasFullLogs = logs && logs.length > 0 && steps;
  let t14;
  if ($[26] !== isJunitTest) {
    t14 = tab => {
      if (isJunitTest) {
        return;
      }
      setActiveTab(tab);
    };
    $[26] = isJunitTest;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const t15 = !isJunitTest;
  let t16;
  if ($[28] !== activeTab || $[29] !== branchFilter || $[30] !== onBranchFilterChange || $[31] !== onTimeRangeChange || $[32] !== onZoomChange || $[33] !== t14 || $[34] !== t15 || $[35] !== timeRangeHours || $[36] !== zoomEnd || $[37] !== zoomStart) {
    t16 = <TestDetailHeader activeTab={activeTab} onTabChange={t14} showTabToggle={t15} timeRangeHours={timeRangeHours} onTimeRangeChange={onTimeRangeChange} branchFilter={branchFilter} onBranchFilterChange={onBranchFilterChange} zoomStart={zoomStart} zoomEnd={zoomEnd} onZoomChange={onZoomChange} />;
    $[28] = activeTab;
    $[29] = branchFilter;
    $[30] = onBranchFilterChange;
    $[31] = onTimeRangeChange;
    $[32] = onZoomChange;
    $[33] = t14;
    $[34] = t15;
    $[35] = timeRangeHours;
    $[36] = zoomEnd;
    $[37] = zoomStart;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  const t17 = activeTab === "logs" ? "opacity-100 pointer-events-auto z-10" : "opacity-0 pointer-events-none z-0";
  let t18;
  if ($[39] !== t17) {
    t18 = cn("absolute inset-0 transition-opacity duration-150 ease-out", t17);
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  const t19 = activeTab !== "logs" ? true : undefined;
  const t20 = activeTab !== "logs";
  let t21;
  if ($[41] !== activeTab || $[42] !== hasFullLogs || $[43] !== isLogsLoading || $[44] !== logs || $[45] !== test.id || $[46] !== test.log_line_end || $[47] !== test.log_line_start || $[48] !== test.logs || $[49] !== test.step_order) {
    t21 = hasFullLogs ? <VirtualizedLogViewer logs={logs} stepOrder={test.step_order} highlightRange={test.log_line_start != null && test.log_line_end != null ? {
      start: test.log_line_start,
      end: test.log_line_end
    } : undefined} isLoading={isLogsLoading} testId={test.id} isVisible={activeTab === "logs"} /> : <div className="h-full overflow-y-auto p-4">{test.logs ? <TestLogViewer logs={test.logs} /> : <div className="flex items-center justify-center h-full text-neutral-500 dark:text-neutral-400 text-sm">No logs available for this test</div>}</div>;
    $[41] = activeTab;
    $[42] = hasFullLogs;
    $[43] = isLogsLoading;
    $[44] = logs;
    $[45] = test.id;
    $[46] = test.log_line_end;
    $[47] = test.log_line_start;
    $[48] = test.logs;
    $[49] = test.step_order;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== t18 || $[52] !== t19 || $[53] !== t20 || $[54] !== t21) {
    t22 = <div className={t18} inert={t19} aria-hidden={t20}>{t21}</div>;
    $[51] = t18;
    $[52] = t19;
    $[53] = t20;
    $[54] = t21;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  const t23 = activeTab === "history" ? "opacity-100 pointer-events-auto z-10" : "opacity-0 pointer-events-none z-0";
  let t24;
  if ($[56] !== t23) {
    t24 = cn("absolute inset-0 overflow-y-auto flex flex-col transition-opacity duration-150 ease-out", t23);
    $[56] = t23;
    $[57] = t24;
  } else {
    t24 = $[57];
  }
  const t25 = activeTab !== "history" ? true : undefined;
  const t26 = activeTab !== "history";
  const t27 = testHistory || null;
  let t28;
  if ($[58] !== actorMap || $[59] !== branchFilter || $[60] !== currentBranch || $[61] !== isFetchingHistory || $[62] !== isLoadingHistory || $[63] !== isResizing || $[64] !== isSuperuser || $[65] !== jobId || $[66] !== maxDuration || $[67] !== onNavigationStart || $[68] !== onRunClick || $[69] !== onZoomChange || $[70] !== org || $[71] !== repository || $[72] !== runId || $[73] !== showCardErrors || $[74] !== t27 || $[75] !== test.duration_seconds || $[76] !== test.id || $[77] !== test.log_line_start || $[78] !== test.logs || $[79] !== timeRangeHours || $[80] !== zoomEnd || $[81] !== zoomStart) {
    t28 = <TestHistoryPanel testHistory={t27} isLoadingHistory={isLoadingHistory} isFetchingHistory={isFetchingHistory} testId={test.id} org={org} repository={repository} runId={runId} jobId={jobId} timeRangeHours={timeRangeHours} branchFilter={branchFilter} currentBranch={currentBranch} zoomStart={zoomStart} zoomEnd={zoomEnd} onZoomChange={onZoomChange} onRunClick={onRunClick} isResizing={isResizing} onNavigationStart={onNavigationStart} showCardErrors={showCardErrors} actorMap={actorMap} isSuperuser={isSuperuser} currentRunLogs={test.logs} currentRunDuration={test.duration_seconds} currentRunMaxDuration={maxDuration} currentRunLogLineStart={test.log_line_start} />;
    $[58] = actorMap;
    $[59] = branchFilter;
    $[60] = currentBranch;
    $[61] = isFetchingHistory;
    $[62] = isLoadingHistory;
    $[63] = isResizing;
    $[64] = isSuperuser;
    $[65] = jobId;
    $[66] = maxDuration;
    $[67] = onNavigationStart;
    $[68] = onRunClick;
    $[69] = onZoomChange;
    $[70] = org;
    $[71] = repository;
    $[72] = runId;
    $[73] = showCardErrors;
    $[74] = t27;
    $[75] = test.duration_seconds;
    $[76] = test.id;
    $[77] = test.log_line_start;
    $[78] = test.logs;
    $[79] = timeRangeHours;
    $[80] = zoomEnd;
    $[81] = zoomStart;
    $[82] = t28;
  } else {
    t28 = $[82];
  }
  let t29;
  if ($[83] !== t24 || $[84] !== t25 || $[85] !== t26 || $[86] !== t28) {
    t29 = <div className={t24} inert={t25} aria-hidden={t26}>{t28}</div>;
    $[83] = t24;
    $[84] = t25;
    $[85] = t26;
    $[86] = t28;
    $[87] = t29;
  } else {
    t29 = $[87];
  }
  let t30;
  if ($[88] !== t22 || $[89] !== t29) {
    t30 = <div className="flex-1 min-h-0 relative">{t22}{t29}</div>;
    $[88] = t22;
    $[89] = t29;
    $[90] = t30;
  } else {
    t30 = $[90];
  }
  let t31;
  if ($[91] !== t16 || $[92] !== t30) {
    t31 = <div className="flex flex-col h-full">{t16}{t30}</div>;
    $[91] = t16;
    $[92] = t30;
    $[93] = t31;
  } else {
    t31 = $[93];
  }
  return t31;
});